"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Instagram, Twitter, ArrowRight, Crown, Globe } from "lucide-react";
import { useAppSelector } from "@/store/hooks";

interface SideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const SideDrawer: React.FC<SideDrawerProps> = ({ isOpen, onClose }) => {
  const pathname = usePathname();
  const { data: content } = useAppSelector((state: any) => state.content);
  const data = content?.royalBlueSideDrawer;

  // Defaults based on the hardcoded values from the previous version
  const logoText = data?.logo?.text || "Royal Blue";
  const description =
    data?.description ||
    "Royal Blue was the first restaurant to open in Egypt, the resturant was designed with the history in mind we have created a soft industrial dining room.";

  const rawSocialLinks = data?.socialLinks || [];
  const instagramUrl = data?.social?.instagram || "#";
  const twitterUrl = data?.social?.twitter || "#";
  const websiteUrl = data?.social?.website || "#";

  const socialLinks =
    Array.isArray(rawSocialLinks) && rawSocialLinks.length > 0
      ? rawSocialLinks
      : [
          { label: "INSTAGRAM", href: instagramUrl },
          { label: "WEBSITE", href: websiteUrl },
          { label: "TWITTER", href: twitterUrl },
        ];

  const getSocialIcon = (label: string) => {
    const l = label.toLowerCase();
    if (l.includes("instagram")) return <Instagram size={17} />;
    if (l.includes("twitter") || l.includes("x")) return <Twitter size={17} />;
    if (l.includes("facebook"))
      return (
        <svg
          className="w-[17px] h-[17px]"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z" />
        </svg>
      );
    return <Globe size={17} />;
  };

  const email = data?.contact?.email || "hello@royalblue.com";
  const phone = data?.contact?.phone || "+1 (234) 567-890";

  const buttonLabel = data?.reservationButton?.label || "Make A Reservation";
  const buttonHref =
    data?.reservationButton?.href ||
    (pathname === "/" ? "#reservation" : "/#reservation");

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/60 z-[60] transition-opacity duration-500 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[380px] bg-[#151515] z-[70] shadow-2xl transition-transform duration-500 ease-in-out transform flex flex-col items-center pt-[90px] pb-[70px] px-10 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-10 right-10 text-white hover:text-[#c59d5f] transition-colors focus:outline-none"
        >
          <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
        </button>

        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          {data?.logo?.icon &&
          (data.logo.icon.startsWith("data:") ||
            data.logo.icon.startsWith("http")) ? (
            <img
              src={data.logo.icon}
              alt="Logo"
              className="w-16 h-16 object-contain mb-2 group-hover:scale-110 transition-transform duration-300"
            />
          ) : (
            <Crown
              className="w-10 h-10 text-[#c59d5f] mb-1"
              strokeWidth={1.5}
            />
          )}
          <h2 className="font-sacramento text-[42px] leading-none text-[#c59d5f]">
            {logoText}
          </h2>
        </div>

        {/* Description Text */}
        <p className="text-[#999999] text-[15px] font-inter leading-[1.8] text-center mb-6">
          {description}
        </p>

        {/* Social Icons */}
        <div className="flex justify-center flex-wrap gap-4 mb-auto w-full">
          {socialLinks.map((link: any, idx: number) => (
            <Link
              key={idx}
              href={link.href || "#"}
              target="_blank"
              className="text-white hover:text-[#c59d5f] transition-colors flex items-center justify-center p-2"
              title={link.label}
            >
              {getSocialIcon(link.label || "")}
            </Link>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="w-full mt-auto">
          {/* Contact Details */}
          <div className="text-left mb-[30px] space-y-1 text-[#999999]">
            <p className="font-inter text-[15px]">
              Main Email: <span className="text-[#999999]">{email}</span>
            </p>
            <p className="font-inter text-[15px]">
              Phone: <span className="text-[#999999]">{phone}</span>
            </p>
          </div>

          {/* Button */}
          <Link
            href={buttonHref}
            onClick={onClose}
            className="flex items-center justify-center w-full h-[52px] bg-[#d29b5c] text-white font-bold font-inter tracking-[0.05em] hover:bg-white hover:text-black transition-colors"
          >
            {buttonLabel}
          </Link>
        </div>
      </div>
    </>
  );
};

export default SideDrawer;
